import { useAuth0 } from '@auth0/auth0-react';
import { useAccessToken, useShowSpinner } from 'hooks';
import { SKIP_ENTITLEMENT_GATE } from 'hooks/localDevBypass';
import jwtDecode from 'jwt-decode';
import { useEffect, useState } from 'react';
import AuthErrorBox from 'ui/AuthErrorBox';
import NoAccessBox from 'ui/NoAccessBox';

const ENTITLEMENTS_NAMESPACE = 'https://dojomojo.com/jwt/entitlements';

type TokenStatus = 'loading' | 'error' | 'loaded';

const UserStoreLoader: React.FC<React.PropsWithChildren> = props => {
  const { children } = props;
  const { user, isLoading } = useAuth0();
  const { getAccessToken } = useAccessToken();
  const [accessToken, setAccessToken] = useState('');
  const [status, setStatus] = useState<TokenStatus>('loading');
  let hasEntitlements = false;

  useShowSpinner({ show: isLoading });

  const getToken = async () => {
    setStatus('loading');
    try {
      const token = await getAccessToken();
      setAccessToken(token.accessToken);
      setStatus('loaded');
    } catch {
      // unrecoverable auth errors already triggered a local logout in
      // useAccessToken, so <App /> redirects to login; anything else
      // (e.g. a network blip) is retryable from the error state below
      setStatus('error');
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    getToken();
  }, []);

  if (accessToken) {
    const decodedToken = jwtDecode(accessToken) as {
      [ENTITLEMENTS_NAMESPACE]: Record<string, unknown>;
    };

    hasEntitlements = Object.keys(decodedToken[ENTITLEMENTS_NAMESPACE] ?? {}).length !== 0;
  }

  if (status === 'error') return <AuthErrorBox onRetry={getToken} />;

  if (!user || isLoading || status !== 'loaded') return null;

  if (!hasEntitlements && !SKIP_ENTITLEMENT_GATE) return <NoAccessBox />;

  return <>{children}</>;
};

export default UserStoreLoader;
